import { Plus } from "lucide-react";
import { CAMPING_YEAR, CLOSURE_WINDOWS, ENTRY_FEES, ENTRY_FEE_DISCLAIMER, formatCOP } from "@/constants";

interface FAQItem {
  q: string;
  a?: string;
  items?: string[];
}

/**
 * Questions ordered by search demand (OpenSEO, Colombia, 2026-10-06):
 * "como llegar al parque tayrona" 210/mes, "parque tayrona horario" 170,
 * "cuando cierra el parque tayrona" 30, "cuanto cuesta entrar" 20, then the
 * camping questions. Edit the text freely; keep the ${...} parts, they pull
 * live fees and closure dates from src/constants.ts.
 */
const faqs: FAQItem[] = [
  {
    q: "¿Cómo llegar al Parque Tayrona?",
    a: "Desde Santa Marta, lo más fácil es tomar el bus que sale del mercado público, en la carrera 9 con calle 11: cuesta unos $9.000 COP y en aproximadamente una hora te deja en la entrada de El Zaino. Desde Cartagena hay buses directos hacia Palomino; pide que te dejen en El Zaino, y cuenta con unas 5 horas de viaje y cerca de $50.000 COP. Si vienes desde Bogotá, lo más práctico es volar a Santa Marta y seguir en bus, porque el bus directo tarda entre 17 y 22 horas.",
  },
  {
    q: "¿Cuál es el horario del Parque Tayrona?",
    a: "El parque abre todos los días, de lunes a domingo, de 8:00 a. m. a 5:00 p. m. Conviene llegar a la entrada apenas abra, sobre todo si vas a caminar hasta Cabo San Juan, porque son unas dos horas y media de sendero y es mejor avanzar antes de que haga más calor. Antes de salir, confirma el horario en el sitio oficial de Parques Nacionales, ya que puede cambiar.",
  },
  {
    q: `¿Cuándo cierra el Parque Tayrona en ${CAMPING_YEAR}?`,
    a: `El parque cierra tres veces al año: ${CLOSURE_WINDOWS.map((w) => `del ${w.full}`).join(", ").replace(/, (?=[^,]*$)/, " y ")}. Durante esos días no se puede entrar bajo ninguna circunstancia, así que, si ya tienes una reserva en esas fechas, habla directamente con tu camping. Encontrarás el motivo de cada cierre en la sección de cierres de esta misma página.`,
  },
  {
    q: "¿Cuánto cuesta acampar en el Parque Tayrona?",
    a: `La entrada para colombianos y residentes cuesta desde ${formatCOP(ENTRY_FEES[1].lowSeasonCOP)} COP en temporada baja hasta ${formatCOP(ENTRY_FEES[1].highSeasonCOP)} COP en temporada alta, y para extranjeros no residentes va de ${formatCOP(ENTRY_FEES[2].lowSeasonCOP)} a ${formatCOP(ENTRY_FEES[2].highSeasonCOP)} COP. A eso se suma un seguro diario obligatorio de $3.000 COP por persona. Para dormir, puedes armar tu propia carpa desde $20.000 COP, alquilar una hamaca por entre $30.000 y $50.000 COP o tomar una carpa ya armada con colchoneta por unos $59.000 COP para dos personas. Si comes dentro del parque, calcula entre $25.000 y $90.000 COP al día. La tabla completa de tarifas está en "Cómo visitar el Parque". ${ENTRY_FEE_DISCLAIMER}`,
  },
  {
    q: "¿Dónde se puede acampar en el Parque Tayrona?",
    a: "Solo en los campings autorizados dentro del parque, como los de Arrecifes y Cabo San Juan que comparamos en esta guía. El camping libre no está permitido: todas las zonas de camping están reguladas, y acampar fuera de ellas puede terminar en una sanción y en la expulsión del parque.",
  },
  {
    q: "¿Es obligatorio reservar en Cabo San Juan?",
    a: "No es obligatorio, pero sí muy recomendable, porque Cabo San Juan es el sector más concurrido del parque. Solo acepta reservas por su página web oficial o en la taquilla el mismo día, y llegar sin reserva después de unas dos horas y media de caminata puede significar quedarte sin espacio. En temporada alta, lo mejor es reservar con al menos dos o tres días de anticipación.",
  },
  {
    q: "¿Se puede nadar en la playa de Arrecifes?",
    a: "No. Arrecifes tiene corrientes muy fuertes y está prohibido bañarse allí. Las playas más cercanas donde sí se puede nadar son Arenillas, a unos 15 minutos a pie, La Piscina y La Piscinita, y en Cabo San Juan también se permite el baño cuando el mar está en condiciones normales.",
  },
  {
    q: "¿Cuántos días se necesitan para visitar bien el Tayrona?",
    a: "Lo ideal son al menos dos días completos, porque así recorres con calma las playas principales, como Arrecifes, La Piscina y Cabo San Juan, sin andar con afán. Con una noche dentro del parque ya puedes llegar a Cabo San Juan y regresar tranquilo al día siguiente. Si solo tienes un día, entra apenas abra el parque para aprovechar el tiempo y caminar antes de que haga más calor.",
  },
  {
    q: "¿Cuál es el camping más barato del Tayrona?",
    a: `Las opciones más económicas son Camping Don Pedro y Camping Jacobo Bermúdez, ambos con hamacas desde $30.000 COP, y EcoCamping LUI, que también es una alternativa de bajo costo. En cuanto a la entrada, la tarifa general más baja es de ${formatCOP(ENTRY_FEES[0].lowSeasonCOP)} COP para colombianos y residentes de 5 a 25 años en temporada baja, y los nacidos en Santa Marta de 5 a 25 años pagan todavía menos: ${formatCOP(ENTRY_FEES[3].lowSeasonCOP)} COP.`,
  },
  {
    q: "¿Carpa o hamaca: cuál es mejor para dormir en el Tayrona?",
    a: "La hamaca es más fresca y liviana de cargar, aunque necesita mosquitero. La carpa, en cambio, te da más privacidad y te protege mejor si llueve, a cambio de ser más calurosa, y suele ser la mejor opción para familias o para quien quiere más espacio. Si alquilas cualquiera de las dos dentro del parque, normalmente ya incluye mosquitero.",
  },
];

// Flattens an item's bullet list + closing sentence into one answer string,
// the same shape screen readers and Googlebot both get from the accordion.
function answerText(item: FAQItem): string {
  const parts: string[] = [];
  if (item.items) parts.push(`${item.items.join(". ")}.`);
  if (item.a) parts.push(item.a);
  return parts.join(" ");
}

// FAQPage structured data built directly from the `faqs` array above — the
// same data the accordion renders — so schema and visible text can't drift
// apart. Rendered directly in JSX (not injected via useEffect) so it's
// present in the prerendered/SSR HTML too, not only after hydration.
function buildFaqJsonLd() {
  const graph = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: answerText(item) },
    })),
  };
  return JSON.stringify(graph);
}

/**
 * FAQ (DESIGN.md): native <details> rows with hairlines. Answers are always
 * in the HTML (crawlable, no JS needed) and match the FAQPage JSON-LD above.
 */
const FAQ = () => (
  <>
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildFaqJsonLd() }} />
    <section id="faq" aria-labelledby="faq-heading" className="pq-section pq-mist">
      <div className="pq-wrap pq-faq-wrap">
        <div style={{ display: "grid", gap: 16, alignContent: "start" }}>
          <h2 id="faq-heading" className="pq-headline">Preguntas frecuentes.</h2>
          <p className="pq-lead">
            Las dudas más comunes antes de acampar en el Tayrona, resueltas.
          </p>
        </div>
        <div className="pq-faq">
          {faqs.map((item) => (
            <details key={item.q}>
              <summary>
                <span>{item.q}</span>
                <Plus size={20} aria-hidden="true" />
              </summary>
              <div className="pq-faq-a">
                {item.items ? (
                  <ul>
                    {item.items.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ul>
                ) : null}
                {item.a ? <p>{item.a}</p> : null}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default FAQ;
